import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useShellTexts } from '../i18n/use-shell-texts';
import { Icon, type IconName } from './icon';
import { TOAST_TIMEOUT_MS, ToastContext, type ToastApi, type ToastType } from './toast-context';

interface ToastItem {
  id: number;
  type: ToastType;
  title: string;
  body?: string;
}

const TONES: Record<ToastType, { className: string; icon: IconName }> = {
  success: { className: 'bg-brand', icon: 'check-circle' },
  error: { className: 'bg-danger', icon: 'times-circle' },
  info: { className: 'bg-info', icon: 'info-circle' },
  warning: { className: 'bg-warning', icon: 'exclamation-triangle' },
};

function Toast({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: number) => void }) {
  const t = useShellTexts().kit;
  const tone = TONES[toast.type];
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(toast.id), TOAST_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [toast.id, onDismiss]);
  return (
    <div
      role={toast.type === 'error' ? 'alert' : 'status'}
      className={`relative w-[300px] cursor-pointer rounded-bs py-4 pr-4 pl-12 text-white opacity-90 shadow-[0_0_12px_#999] transition-opacity hover:opacity-100 ${tone.className}`}
      onClick={() => onDismiss(toast.id)}
    >
      <Icon name={tone.icon} className="absolute top-1/2 left-4 -translate-y-1/2 text-2xl" />
      <div className="font-bold">{toast.title}</div>
      {toast.body ? <div className="break-words">{toast.body}</div> : null}
      <button type="button" className="sr-only" onClick={() => onDismiss(toast.id)}>
        {t.dismiss}
      </button>
    </div>
  );
}

/**
 * The legacy toaster: fixed top-right, 300px wide, solid colour, gone after five seconds or on a
 * click. Mount once at the root; features call `useToast()`.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const t = useShellTexts().kit;
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(0);
  const dismiss = useCallback(
    (id: number) => setToasts(current => current.filter(toast => toast.id !== id)),
    [],
  );
  const api = useMemo<ToastApi>(() => {
    const pop = (type: ToastType, title: string, body?: string) => {
      const id = ++nextId.current;
      setToasts(current => [...current, { id, type, title, body }]);
    };
    return {
      pop,
      success: (title, body) => pop('success', title, body),
      error: (title, body) => pop('error', title, body),
      info: (title, body) => pop('info', title, body),
      warning: (title, body) => pop('warning', title, body),
    };
  }, []);
  return (
    <ToastContext.Provider value={api}>
      {children}
      <section
        aria-label={t.notifications}
        className="fixed top-3 right-3 z-[3000] flex flex-col gap-2 text-sm"
      >
        {toasts.map(toast => (
          <Toast key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </section>
    </ToastContext.Provider>
  );
}
